import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import { Guid } from '../../services/core';
import { ConfigurationService, IAppSettings } from '../../services/configuration/configuration.service';
import { OirHttpService } from '../../services/http/oir-http.service';
import { ModuleEnum, Navigation, Node } from './navigation.types';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  navModel$ = new BehaviorSubject<Navigation>(null);

  previousNode: Node;
  currentNode: Node;
  currentParent: Node;
  model: Navigation;

  constructor(
    private readonly router: Router,
    private readonly http: OirHttpService,
    private readonly configuration: ConfigurationService<IAppSettings>
  ) {}

  init(navigation: Navigation): any {
    this.model = navigation;

    if (this.model.tree.length) {
      const activeNode = this.model.tree.find(n => this.router.isActive(n.path, false))
      if (activeNode) {
        this.select(activeNode, true);
      } else {
        this.select(this.model.tree[0], true);
      }
    } else {
      this.navModel$.next(this.model);
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        for (const node of this.model.tree) {
          if (this.router.isActive(node.path, false)) {
            this.select(node);
          } else {
            node.selected = false;
          }
        }
      }
    });

    this.initAppSwitcher();
  }

  navigateToPath(path: string): void {
    let node = this.model.tree.find((x) => x.path === path);
    if (!node) {
      node = this.model.tree[0];
    }
    this.select(node);
    this.router.navigate([path]);
  }

  addNode(node: Node): void {
    const currentNode = !this.currentNode.isStatic
      ? this.currentNode
      : this.currentParent;

    if (currentNode) {
      node.parentId = this.currentNode.id;
      node.expanded = true;

      currentNode.children = [node];
      currentNode.expanded = true;

      this.select(node);
    }
  }

  removeChildrenOutsidePath(path): void {
    if (this.model) {
      this.model.tree.forEach((element) => {
        if (element.path !== path) {
          element.children = [];
        }
      });
    }
  }

  select(node: Node, isParent?): void {
    if (this.currentNode && this.currentNode.children) {
      this.currentNode.selected = false;
    }

    this.previousNode = this.currentNode;
    this.currentNode = node;
    this.currentNode.selected = true;

    if (isParent && !node.isStatic) {
      this.currentParent = node;
    }

    this.navModel$.next(this.model);
  }

  clearSelect(node: Node): void {
    if (this.currentNode && this.currentParent && !node.isStatic) {
      this.currentParent.expanded = false;
    }

    this.select(node, true);

    if (node.children && node.children.length > 0 && !node.isStatic) {
        this.currentParent.children = [];
    }
  }

  toggle(node: Node): void {
    if (node.children && node.children.length > 0) {
      node.expanded = !node.expanded;
    }
  }

  back(): void {
    const parentNode = this.getParentNode(this.model.tree);
    if (parentNode) {
      this.currentParent === parentNode
        ? this.clearSelect(parentNode)
        : this.select(parentNode);

      this.router.navigate([parentNode.path]);
    }
  }

  expandHead(): void {
    this.model.module.selected = !this.model.module.selected;
    this.model.module.expanded = !this.model.module.expanded;
  }

  initAppSwitcher(): void {
    this.configuration
      .get('UserName')
      .pipe(
        switchMap((userName) =>
          this.http.get<any>({
            requestUrl: 'CommsEndpoint',
            path: `/api/Dashboard/GetUserApplications?userAd=${userName}`,
            hideSpinner: true
          })
        ),
        tap((node) => {
          this.model.module.children = [];
          node.forEach(item => {
            const itemTitle = item.title && item.title.length > 0 ? item.title : 'MODULE.' + ModuleEnum[item.id].toUpperCase();

            if (itemTitle === this.model.module.title) { // dont add option from its own module
              return;
            }
            const dashboardUrls= [
              "https://localhost:44335/",
              "https://proceduralsystemdev.azurewebsites.net",
              "https://bpt-int-proceduralsystem.azurewebsites.net",
              "https://test-proceduralsystem.oir.ie",
              "https://proceduralsystemsystemtest.azurewebsites.net",
              "https://bpt-proceduralsystem.oir.ie",
              "https://proceduralsystembpt.azurewebsites.net",
              "https://bpt-int-proceduralsystem.azurewebsites.net/",
              "https://prelive-proceduralsystem.oir.ie",
              "https://proceduralsystem.oir.ie"
            ];
            if (item.module === ModuleEnum.Dashboard) {
              this.model.homeUrl = item.path;
              this.model.isDashboard = dashboardUrls.filter(p=> p.indexOf(window.location.origin)>-1).length > 0;
              return;
            }
            this.model.module.children.push({title: itemTitle, path: item.path, isStatic: false, children: [], id: Guid.newGuid()});
          });
        })
      )
      .subscribe();
  }

  private getParentNode(nodes: Array<Node>): Node {
    let result: Node;

    for (const node of nodes) {
      const item = node;

      if (item.id === this.currentNode.parentId) {
        result = item;
      } else if (item.children) {
        result = this.getParentNode(item.children);
      }

      if (result) {
        break;
      }
    }

    return result;
  }
}
